// SPDX-License-Identifier: BUSL-1.1

/**
 * Session message input — send a message to a running session.
 *
 * Rendered below the DataTable on the agent detail page, just above
 * the LiveStreamPanel. Uses the POST /api/sessions/:id/messages endpoint.
 *
 */

import { useRef, type ComponentRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { Send } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { showSuccess, showApiError } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import type { SessionStatus } from '@/types/api';

/* ------------------------------------------------------------------ */
/*  Schema + types                                                     */
/* ------------------------------------------------------------------ */

const messageSchema = z.object({
  text: z
    .string()
    .min(1, 'Message cannot be empty')
    .max(10000, 'Message too long'),
});

type MessageFormValues = z.infer<typeof messageSchema>;

interface SessionMessageInputProps {
  sessionId: string | null;
  sessionStatus?: SessionStatus;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function SessionMessageInput({
  sessionId,
  sessionStatus,
}: SessionMessageInputProps) {
  const textareaRef = useRef<ComponentRef<typeof Textarea> | null>(null);

  const form = useForm<MessageFormValues>({
    resolver: zodResolver(messageSchema),
    defaultValues: { text: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: MessageFormValues) =>
      apiFetch(`/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ text: values.text }),
      }),
    onSuccess: () => {
      showSuccess('Message sent');
      form.reset({ text: '' });
      textareaRef.current?.focus();
    },
    onError: (err) => showApiError(err),
  });

  // Empty state: no session selected — render nothing.
  if (!sessionId) return null;

  const canSend =
    sessionStatus === 'running' || sessionStatus === 'idle';
  const disabled = !canSend || mutation.isPending;

  const submit = form.handleSubmit((values) => mutation.mutate(values));

  return (
    <div className="mt-4 rounded-lg border border-neutral-800 bg-neutral-900 p-4">
      <Form {...form}>
        <form onSubmit={submit}>
          <FormField
            control={form.control}
            name="text"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    ref={(el) => {
                      field.ref(el);
                      textareaRef.current = el;
                    }}
                    placeholder="Type a message to the agent..."
                    disabled={disabled}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        (e.ctrlKey || e.metaKey)
                      ) {
                        e.preventDefault();
                        void submit();
                      }
                    }}
                    className="min-h-[80px] resize-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-neutral-500">
              {canSend
                ? 'Ctrl+Enter to send'
                : `Cannot send — session is ${sessionStatus ?? 'unknown'}`}
            </span>
            <Button
              type="submit"
              disabled={disabled}
              className="gap-1.5"
            >
              <Send className="size-3.5" />
              {mutation.isPending ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
